import { useState, useRef } from "react";
import { Paperclip, FileText, Loader2, X, Square } from "lucide-react";
import { ALLOWED_EXTENSIONS } from "../utils/fileHelpers";
import TiptapInput from "./TiptapInput";
import RichTextToolbar from "./RichTextToolbar";
import EmojiPicker from "./EmojiPicker";
import ExportThread from "./ExportThread";
import DropDownPromptLoader from "./DropDownPromptLoader";
import DropDownNotes from "./DropDownNotes";
import DropDownModelSelector from "./DropDownModelSelector";
import TokenCounter from "./TokenCounter";
import MentionDropdown from "./MentionDropdown";

const ChatEntry = (props) => {
  const [isAgentSelectorOpen, setIsAgentSelectorOpen] = useState(false);
  const [isPromptLoaderOpen, setIsPromptLoaderOpen] = useState(false);
  const [isDropDownNotesOpen, setIsDropDownNotesOpen] = useState(false);
  const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [isModelSelectorOpen, setIsModelSelectorOpen] = useState(false);
  const editorRef = useRef(null);
  const [mentionedUuids, setMentionedUuids] = useState([]);
  const chatTypingTimeoutRef = useRef(null);

  const handleMention = (uuid) => {
    setMentionedUuids((prev) => (prev.includes(uuid) ? prev : [...prev, uuid]));
  };

  const closeAllDropdowns = () => {
    setIsAgentSelectorOpen(false);
    setIsPromptLoaderOpen(false);
    setIsDropDownNotesOpen(false);
    setIsEmojiPickerOpen(false);
    setIsExportOpen(false);
    setIsModelSelectorOpen(false);
  };

  const handleEmojiSelect = (emoji) => {
    const ed = editorRef.current?.editor;
    if (ed) {
      ed.chain().focus().insertContent(emoji).run();
    } else {
      props.setChatEntry(props.chatEntry + emoji);
    }
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = "none";
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleFileInputChange = (e) => {
    const files = e.target.files;
    if (files?.length > 0) {
      props.onFilesSelected(files);
    }
    e.target.value = "";
  };

  const toolbarDivider = <div className="w-px h-4 bg-dropdownDivider mx-1.5" />;

  const attachButton = (
    <button
      onClick={() => props.fileInputRef?.current?.click()}
      className="inline-flex items-center gap-1.5 py-1 text-mutedText hover:text-mainText transition-colors cursor-pointer"
      title="Attach files"
    >
      <Paperclip className="h-3.5 w-3.5" />
      <span className="text-[13px] font-light">Attach</span>
    </button>
  );

  return (
    <div
      className="w-full"
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {!props.compact && (
        <input
          ref={props.fileInputRef}
          type="file"
          multiple
          className="hidden"
          accept={Array.from(ALLOWED_EXTENSIONS)
            .map((ext) => `.${ext}`)
            .join(",")}
          onChange={handleFileInputChange}
        />
      )}

      <div className="w-full relative bg-surface/80 border-t border-surfaceBorder">
        {!props.compact && (
          <div className="flex items-center pl-5 pr-3 pt-2 pb-1.5">
            {props.chatType === "team" ? (
              <>
                <RichTextToolbar editor={editorRef.current?.editor} />
                {toolbarDivider}
                <EmojiPicker
                  isOpen={isEmojiPickerOpen}
                  setIsOpen={setIsEmojiPickerOpen}
                  onSelect={handleEmojiSelect}
                  closeOthers={() => {
                    setIsExportOpen(false);
                  }}
                />
                {toolbarDivider}
                <ExportThread
                  messages={props.messages}
                  isOpen={isExportOpen}
                  setIsOpen={setIsExportOpen}
                  closeOthers={() => {
                    setIsEmojiPickerOpen(false);
                  }}
                />
                {/* {toolbarDivider}
                {attachButton} */}
              </>
            ) : props.chatType === "public" ? (
              <>
                <DropDownModelSelector
                  models={props.publicModels || []}
                  selectedModel={props.selectedModel || ""}
                  onSelect={props.onSelectModel}
                  isOpen={isModelSelectorOpen}
                  onToggle={() => setIsModelSelectorOpen(!isModelSelectorOpen)}
                  closeOthers={() => {
                    setIsEmojiPickerOpen(false);
                    setIsExportOpen(false);
                  }}
                  favorites={props.favoriteModels || []}
                  onToggleFavorite={props.onToggleFavorite}
                />
                {toolbarDivider}
                <TokenCounter
                  systemPrompt={props.systemPrompt || ""}
                  currentMessage={props.chatEntry || ""}
                  messages={props.messages || []}
                  contextWindow={props.contextWindow || 128000}
                  historyDepth={props.historyDepth ?? 5}
                  onChangeDepth={props.onChangeDepth}
                />
                {toolbarDivider}
                <RichTextToolbar editor={editorRef.current?.editor} />
                {toolbarDivider}
                <EmojiPicker
                  isOpen={isEmojiPickerOpen}
                  setIsOpen={setIsEmojiPickerOpen}
                  onSelect={handleEmojiSelect}
                  closeOthers={() => {
                    setIsModelSelectorOpen(false);
                    setIsExportOpen(false);
                  }}
                />
                {toolbarDivider}
                <ExportThread
                  messages={props.messages}
                  isOpen={isExportOpen}
                  setIsOpen={setIsExportOpen}
                  closeOthers={() => {
                    setIsModelSelectorOpen(false);
                    setIsEmojiPickerOpen(false);
                  }}
                />
                {/* {toolbarDivider}
                {attachButton} */}
              </>
            ) : (
              <>
                <DropDownPromptLoader
                  chatEntry={props.chatEntry}
                  setChatEntry={props.setChatEntry}
                  isAgentSelectorOpen={isAgentSelectorOpen}
                  setIsAgentSelectorOpen={setIsAgentSelectorOpen}
                  isPromptLoaderOpen={isPromptLoaderOpen}
                  setIsPromptLoaderOpen={setIsPromptLoaderOpen}
                  setIsDropDownNotesOpen={setIsDropDownNotesOpen}
                  isDropDownNotesOpen={isDropDownNotesOpen}
                />
                {toolbarDivider}
                <DropDownNotes
                  isDropDownNotesOpen={isDropDownNotesOpen}
                  setIsDropDownNotesOpen={setIsDropDownNotesOpen}
                  setIsAgentSelectorOpen={setIsAgentSelectorOpen}
                  setIsPromptLoaderOpen={setIsPromptLoaderOpen}
                />
                {toolbarDivider}
                <RichTextToolbar editor={editorRef.current?.editor} />
                {toolbarDivider}
                <EmojiPicker
                  isOpen={isEmojiPickerOpen}
                  setIsOpen={setIsEmojiPickerOpen}
                  onSelect={handleEmojiSelect}
                  closeOthers={() => {
                    setIsAgentSelectorOpen(false);
                    setIsPromptLoaderOpen(false);
                    setIsDropDownNotesOpen(false);
                    setIsExportOpen(false);
                  }}
                />
                {toolbarDivider}
                <ExportThread
                  messages={props.messages}
                  isOpen={isExportOpen}
                  setIsOpen={setIsExportOpen}
                  closeOthers={() => {
                    setIsAgentSelectorOpen(false);
                    setIsPromptLoaderOpen(false);
                    setIsDropDownNotesOpen(false);
                    setIsEmojiPickerOpen(false);
                  }}
                />
                {/* {toolbarDivider}
                {attachButton} */}
              </>
            )}
          </div>
        )}

        {!props.compact && props.attachedFiles?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 px-5 pb-1.5">
            {props.attachedFiles.map((af) => (
              <div
                key={af.id}
                className={[
                  "inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-[12px] font-light transition-colors",
                  af.status === "error"
                    ? "border-logoutColor/40 bg-logoutColor/10 text-logoutColor"
                    : "border-inputBorder bg-transparent text-mutedText",
                ].join(" ")}
              >
                {af.status === "uploading" ? (
                  <Loader2 className="h-3 w-3 animate-spin flex-shrink-0" />
                ) : af.status === "error" ? (
                  <X className="h-3 w-3 flex-shrink-0" />
                ) : (
                  <FileText className="h-3 w-3 flex-shrink-0" />
                )}
                <span className="truncate max-w-[150px]">
                  {af.status === "error" ? af.error : af.name}
                </span>
                <button
                  onClick={() => props.onRemoveFile(af.id)}
                  className="ml-0.5 rounded p-0.5 hover:bg-btnHover transition-colors cursor-pointer"
                  aria-label="Remove file"
                >
                  <X className="h-3 w-3" strokeWidth={2} />
                </button>
              </div>
            ))}
          </div>
        )}

        {!props.compact && <div className="h-px bg-dropdownDivider mx-3" />}

        <div
          className={`relative ${props.compact ? "px-2 pt-1.5 pb-4" : "px-3 pt-2 pb-10"}`}
          onClick={closeAllDropdowns}
        >
          {!props.compact && props.threadLocked && (
            <div className="absolute inset-0 z-[70] flex items-center justify-between px-5 bg-surface/90 backdrop-blur-sm rounded-lg">
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 rounded-full bg-velBlue/20 flex items-center justify-center">
                  <span className="text-[9px] font-medium text-velBlue">
                    {(props.threadLockedByName || "?")
                      .slice(0, 2)
                      .toUpperCase()}
                  </span>
                </div>
                <span className="text-[13px] font-light text-mutedText">
                  {props.threadLockedByName || "Someone"} is getting a response…
                </span>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  props.onUnlockThread?.();
                }}
                className="text-[12px] font-light text-mutedText hover:text-mainText transition-colors cursor-pointer px-2 py-1 rounded hover:bg-btnHover"
              >
                Unlock
              </button>
            </div>
          )}
          {props.chatType === "team" && (
            <MentionDropdown
              editorRef={editorRef}
              teamId={props.currentTeamId}
              onMention={handleMention}
            />
          )}
          <TiptapInput
            chatEntry={props.chatEntry}
            setChatEntry={props.setChatEntry}
            selectedAgent={props.selectedAgent}
            sendPayload={
              props.chatType === "team"
                ? (msg) => {
                    props.onTeamSend(msg, mentionedUuids);
                    setMentionedUuids([]);
                    props.setChatEntry("");
                    const ed = editorRef.current?.editor;
                    if (ed) ed.commands.clearContent();
                  }
                : (...args) => {
                    props.onChatStopTyping?.();
                    clearTimeout(chatTypingTimeoutRef.current);
                    props.sendPayload(...args);
                  }
            }
            currentThreadId={props.currentThreadId}
            sentHistory={props.sentHistory}
            editorRef={editorRef}
            onInputChange={
              props.chatType === "team"
                ? props.onTeamInputChange
                : props.onChatTyping
                  ? (text) => {
                      props.onChatTyping();
                      clearTimeout(chatTypingTimeoutRef.current);
                      chatTypingTimeoutRef.current = setTimeout(() => {
                        props.onChatStopTyping?.();
                      }, 2000);
                    }
                  : undefined
            }
          />
        </div>

        {props.isStreaming && (
          <div className="flex items-center justify-end px-3 py-1.5">
            <button
              aria-label="Stop generating"
              onClick={props.onStop}
              className="px-2.5 py-1.5 text-logoutColor hover:text-logoutColor/80 transition-colors cursor-pointer"
            >
              <Square
                className="h-[16px] w-[16px]"
                fill="currentColor"
                strokeWidth={0}
              />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatEntry;